import { keycloakConfig, keycloakUrls } from "@Config/keycloak";
import { usersApi } from "@Api/users";

import type { AuthorizedUserInfo } from "@Types/users";

export interface TokenResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  tokenType: string;
  scope: string;
}
export type UserInfo = AuthorizedUserInfo;

class KeycloakService {
  private readonly CODE_VERIFIER_KEY = "login_code_verifier";
  private readonly ACCESS_TOKEN_KEY = "login_access_token";
  private readonly REFRESH_TOKEN_KEY = "login_refresh_token";
  private readonly USER_INFO_KEY = "login_user_info";
  private readonly TOKEN_EXPIRES_KEY = "login_token_expires";

  public readonly KC_SESSION_STATE_KEY = "session_state";

  // Race condition protection for ensureValidToken
  private ensureValidTokenPromise: Promise<boolean> | null = null;

  // Gerar code verifier para PKCE
  private generateCodeVerifier(): string {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return btoa(String.fromCharCode.apply(null, Array.from(array)))
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=/g, "");
  }

  // Gerar code challenge para PKCE
  private async generateCodeChallenge(verifier: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const digest = await crypto.subtle.digest("SHA-256", data);
    return btoa(
      String.fromCharCode.apply(null, Array.from(new Uint8Array(digest)))
    )
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=/g, "");
  }

  // Gerar state aleatório
  private generateState(): string {
    const array = new Uint8Array(16);
    crypto.getRandomValues(array);
    return btoa(String.fromCharCode.apply(null, Array.from(array)));
  }

  // Redirecionar para página de login do Keycloak
  async redirectToLogin(): Promise<void> {
    const codeVerifier = this.generateCodeVerifier();
    await this.generateCodeChallenge(codeVerifier);
    const state = this.generateState();

    localStorage.setItem("login_state", state);

    const params = new URLSearchParams({
      response_type: "code",
      client_id: keycloakConfig.clientId,
      redirect_uri: keycloakConfig.redirectUri,
      scope: "openid",
      state: state,
    });

    window.location.href = `${keycloakUrls.auth}?${params.toString()}`;
  }

  // Trocar código de autorização por tokens
  async exchangeCodeForTokens(
    code: string,
    state: string
  ): Promise<TokenResponse> {
    const savedState = localStorage.getItem("login_state");
    if (state !== savedState) {
      throw new Error("Invalid state authenticate!");
    }

    localStorage.setItem("login_session_state", state);

    const params = new URLSearchParams({
      grantType: "authorization_code",
      clientId: keycloakConfig.clientId,
      code: code,
      scope: "email openid",
      redirectUri: keycloakConfig.redirectUri,
    });

    const response = await fetch(keycloakUrls.token, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "X-Realm-Name": keycloakConfig.realm,
      },
      body: params.toString(),
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Token exchange failed: ${error}`);
    }

    const tokenData: TokenResponse = await response.json();

    // Salvar tokens
    this.saveTokens(tokenData);

    // Limpar dados temporários
    localStorage.removeItem(this.CODE_VERIFIER_KEY);
    localStorage.removeItem("login_state");

    return tokenData;
  }

  // Salvar tokens no localStorage
  public saveTokens(tokenData: TokenResponse): void {
    const expiresAt = Date.now() + tokenData.expiresIn * 1000;

    localStorage.setItem(this.ACCESS_TOKEN_KEY, tokenData.accessToken);
    localStorage.setItem(this.REFRESH_TOKEN_KEY, tokenData.refreshToken);
    localStorage.setItem(this.TOKEN_EXPIRES_KEY, expiresAt.toString());
    localStorage.setItem("isAuthenticated", "true");
  }

  // Obter informações do usuário
  async getUserInfo(): Promise<UserInfo> {
    const accessToken = this.getAccessToken();
    if (!accessToken) {
      throw new Error("No access token available");
    }

    localStorage.removeItem(this.USER_INFO_KEY);

    const userInfo = await usersApi.getAuthorizedUser(accessToken);

    localStorage.setItem(this.USER_INFO_KEY, JSON.stringify(userInfo));

    return userInfo;
  }

  // Single-flight do refresh: garante UMA renovação em voo. Sem isso, dois
  // requests concorrentes (dois axios / interceptor + handle401) gastam o MESMO
  // refresh token; com rotação de refresh token no Keycloak, o 2º uso falha e
  // derrubava a sessão. Todas as chamadas compartilham a mesma Promise.
  private refreshPromise: Promise<TokenResponse> | null = null;

  // Renovar token usando refresh token
  async refreshToken(): Promise<TokenResponse> {
    if (this.refreshPromise) return this.refreshPromise;

    this.refreshPromise = (async () => {
      const refreshToken = localStorage.getItem(this.REFRESH_TOKEN_KEY);
      if (!refreshToken) {
        throw new Error("No refresh token available");
      }

      const params = new URLSearchParams({
        grantType: "refresh_token",
        clientId: keycloakConfig.clientId,
        refreshToken: refreshToken,
      });

      const response = await fetch(keycloakUrls.token, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "X-Realm-Name": keycloakConfig.realm,
        },
        body: params.toString(),
      });

      if (!response.ok) {
        // NÃO faz logout aqui: quem chama decide a UX (handle401 → modal de
        // sessão; ensureValidToken → segue e o 401 recupera). Um refresh que
        // falha por transiente/corrida não deve redirecionar direto ao /logout.
        throw new Error("Token refresh failed");
      }

      const tokenData: TokenResponse = await response.json();
      this.saveTokens(tokenData);
      return tokenData;
    })().finally(() => {
      this.refreshPromise = null;
    });

    return this.refreshPromise;
  }

  // Verificar se token é válido
  isTokenValid(): boolean {
    const accessToken = localStorage.getItem(this.ACCESS_TOKEN_KEY);
    const expiresAt = localStorage.getItem(this.TOKEN_EXPIRES_KEY);

    if (!accessToken || !expiresAt) {
      return false;
    }

    const now = Date.now();
    const expires = parseInt(expiresAt);

    // Margem de 30s antes do vencimento. ATENÇÃO: a margem tem que ser MENOR que
    // o tempo de vida do access token (padrão Keycloak = 5min), senão o token
    // nasce "inválido" e dispara refresh a CADA request/navegação — o que causava
    // corrida de rotação do refresh token e queda de sessão logo após o login.
    return expires > now + 30 * 1000;
  }

  // Obter access token
  getAccessToken(): string | null {
    return localStorage.getItem(this.ACCESS_TOKEN_KEY);
  }

  // Obter informações do usuário salvas
  getSavedUserInfo(): UserInfo | null {
    const userInfo = localStorage.getItem(this.USER_INFO_KEY);
    return userInfo ? JSON.parse(userInfo) : null;
  }

  // Verificar se usuário está autenticado
  isAuthenticated(): boolean {
    return (
      localStorage.getItem("isAuthenticated") === "true" && this.isTokenValid()
    );
  }

  // Logout
  async logout(): Promise<void> {
    // Logout no Keycloak
    try {
      const savedState =
        localStorage.getItem(this.KC_SESSION_STATE_KEY) || "";
      await fetch(keycloakUrls.logout + "/" + savedState, {
        method: "DELETE",
        headers: {
          "X-Realm-Name": keycloakConfig.realm,
        },
      });
      localStorage.removeItem(this.KC_SESSION_STATE_KEY);
    } catch (error) {
      console.error("Error during Keycloak logout:", error);
    }

    // Limpar dados locais
    localStorage.removeItem(this.ACCESS_TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.USER_INFO_KEY);
    localStorage.removeItem(this.TOKEN_EXPIRES_KEY);
    localStorage.removeItem("isAuthenticated");
    localStorage.removeItem("userName");
    localStorage.removeItem("userEmail");

    // Sessão encerrada: vai para a tela de saída (NÃO redireciona direto ao
    // Keycloak). A rota de retorno (login_original_path) é gerida pelo chamador:
    // logout deliberado limpa; queda de sessão preserva para voltar depois.
    window.location.href = "/logout";
  }

  // Tentar renovar token automaticamente se necessário
  async ensureValidToken(): Promise<boolean> {
    // Se já existe uma chamada em andamento, reutilizar a mesma Promise
    if (this.ensureValidTokenPromise) {
      return this.ensureValidTokenPromise;
    }

    // Token ainda válido? Retornar imediatamente
    if (this.isTokenValid()) {
      return true;
    }

    // Criar nova Promise para renovação
    this.ensureValidTokenPromise = (async () => {
      try {
        await this.refreshToken();
        return true;
      } catch (error) {
        console.error("Failed to refresh token:", error);
        return false;
      } finally {
        // Limpar a Promise após conclusão para permitir futuras chamadas
        this.ensureValidTokenPromise = null;
      }
    })();

    return this.ensureValidTokenPromise;
  }
}

export const keycloakService = new KeycloakService();
